import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Lock,
  CreditCard,
  Trash2,
  Save,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  Coins,
  History,
  Settings as SettingsIcon
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';

export default function UserProfile() {
  const { user, updateProfile, changePassword, deleteAccount } = useAuth();
  const { credits, history, loading: creditsLoading } = useCredits();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const tabs = [
    { id: 'profile', name: 'Profilo', icon: User },
    { id: 'security', name: 'Sicurezza', icon: Lock },
    { id: 'credits', name: 'Crediti', icon: Coins },
    { id: 'billing', name: 'Pagamenti', icon: CreditCard },
    { id: 'danger', name: 'Zona Pericolosa', icon: AlertTriangle }
  ];

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(profileData);
      setMessage({ type: 'success', text: 'Profilo aggiornato con successo!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Errore nell\'aggiornamento del profilo' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Le password non coincidono' });
      return;
    }
    setLoading(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setMessage({ type: 'success', text: 'Password cambiata con successo!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Errore nel cambio password' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Sei sicuro di voler eliminare il tuo account? Questa azione è irreversibile.')) {
      setLoading(true);
      try {
        await deleteAccount();
      } catch (error) {
        setMessage({ type: 'error', text: error.message || 'Errore nell\'eliminazione dell\'account' });
        setLoading(false);
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 pb-12 relative">
      {/* Sfondo sfumato per la pagina profilo */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] pointer-events-none opacity-20" style={{ background: 'radial-gradient(ellipse, var(--primary) 0%, transparent 70%)', filter: 'blur(120px)', zIndex: -1 }} />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10 text-center md:text-left"
      >
        <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' }}>
          Impostazioni Account
        </h1>
        <p className="text-base" style={{ color: 'rgba(255,255,255,0.45)' }}>Gestisci il tuo profilo, la sicurezza e le tue preferenze</p>
      </motion.div>

      {/* Message */}
      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-xl border ${
            message.type === 'success'
              ? 'bg-green-500/20 border-green-500/30 text-green-300'
              : 'bg-red-500/20 border-red-500/30 text-red-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
            {message.text}
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:col-span-1"
        >
          <div className="panel p-6 rounded-[24px]">
            <div className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                      isActive
                        ? 'text-white'
                        : 'text-white/50 hover:text-white hover:bg-white/5'
                    }`}
                    style={isActive ? {
                      background: 'linear-gradient(145deg, rgba(59,130,246,0.1), rgba(139,92,246,0.05))',
                      border: '1px solid rgba(255,255,255,0.1)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)'
                    } : { border: '1px solid transparent' }}
                  >
                    <div className={`icon-box w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'primary' : ''}`}>
                      <Icon size={16} style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.6)' }} />
                    </div>
                    <span className="font-semibold text-sm">{tab.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:col-span-3"
        >
          <div className="panel p-8 md:p-10 rounded-[32px]">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>
                  <div className="icon-box primary w-10 h-10 rounded-xl">
                    <User size={18} />
                  </div>
                  Informazioni Profilo
                </h2>
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-sm block mb-2">Nome Utente</label>
                      <input
                        type="text"
                        value={profileData.username}
                        onChange={(e) => setProfileData({...profileData, username: e.target.value})}
                        className="input-field w-full px-4 py-3.5 rounded-xl text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="label-sm block mb-2">Email</label>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                        className="input-field w-full px-4 py-3.5 rounded-xl text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="label-sm block mb-2">Nome</label>
                      <input
                        type="text"
                        value={profileData.firstName}
                        onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                        className="input-field w-full px-4 py-3.5 rounded-xl text-sm"
                      />
                    </div>
                    <div>
                      <label className="label-sm block mb-2">Cognome</label>
                      <input
                        type="text"
                        value={profileData.lastName}
                        onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                        className="input-field w-full px-4 py-3.5 rounded-xl text-sm"
                      />
                    </div>
                  </div>
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="generate-btn rounded-xl py-3.5 px-8 inline-flex w-auto"
                    >
                      <Save size={16} />
                      {loading ? 'Salvando...' : 'Salva Modifiche'}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>
                  <div className="icon-box primary w-10 h-10 rounded-xl">
                    <Lock size={18} />
                  </div>
                  Sicurezza
                </h2>
                <form onSubmit={handlePasswordChange} className="space-y-6 max-w-md">
                  <div>
                    <label className="label-sm block mb-2">Password Attuale</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        className="input-field w-full px-4 py-3.5 rounded-xl text-sm pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="label-sm block mb-2">Nuova Password</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="input-field w-full px-4 py-3.5 rounded-xl text-sm pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                      >
                        {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="label-sm block mb-2">Conferma Nuova Password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        className="input-field w-full px-4 py-3.5 rounded-xl text-sm pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="generate-btn rounded-xl py-3.5 px-8 inline-flex w-auto"
                    >
                      <Lock size={16} />
                      {loading ? 'Cambiando...' : 'Cambia Password'}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Credits Tab */}
            {activeTab === 'credits' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>
                  <div className="icon-box primary w-10 h-10 rounded-xl">
                    <Coins size={18} />
                  </div>
                  Gestione Crediti
                </h2>
                <div className="space-y-8">
                  {/* Current Balance */}
                  <div className="relative overflow-hidden rounded-[24px] p-8 border border-white/10" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.1) 0%, rgba(255,255,255,0.02) 100%)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)' }}>
                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-yellow-500/20 rounded-full blur-[50px] pointer-events-none" />
                    <div className="flex items-center justify-between relative z-10">
                      <div>
                        <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Saldo Attuale</h3>
                        <p className="text-sm text-white/50">Crediti disponibili per generazioni</p>
                      </div>
                      <div className="text-right">
                        <div className="text-4xl font-bold" style={{ color: '#FFD700', fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' }}>
                          {creditsLoading ? '...' : credits?.toLocaleString() || '0'}
                        </div>
                        <div className="text-xs uppercase tracking-[0.15em] text-yellow-500/50 mt-1">crediti</div>
                      </div>
                    </div>
                  </div>

                  {/* Buy Credits */}
                  <div className="glass p-8 rounded-[24px]">
                    <h3 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Acquista Crediti</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      {[
                        { amount: 100, price: '€9.99', popular: false },
                        { amount: 500, price: '€39.99', popular: true },
                        { amount: 1000, price: '€69.99', popular: false }
                      ].map((pack) => (
                        <div
                          key={pack.amount}
                          className="relative p-6 rounded-2xl border transition-all duration-300 cursor-pointer group"
                          style={pack.popular ? {
                            background: 'rgba(255,255,255,0.08)',
                            borderColor: 'rgba(59,130,246,0.5)',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                          } : {
                            background: 'rgba(255,255,255,0.02)',
                            borderColor: 'rgba(255,255,255,0.08)'
                          }}
                        >
                          {pack.popular && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                              <div className="pill-active px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                Popolare
                              </div>
                            </div>
                          )}
                          <div className="text-center">
                            <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{pack.amount}</div>
                            <div className="text-xs text-white/40 mb-4 uppercase tracking-widest">crediti</div>
                            <div className="text-xl font-bold" style={{ color: pack.popular ? '#3b82f6' : 'rgba(255,255,255,0.8)' }}>{pack.price}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Credit History */}
                  <div className="glass p-8 rounded-[24px]">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                      <History size={18} className="text-white/50" />
                      Cronologia Crediti
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto modern-dark-scrollbar pr-2">
                      {history?.length > 0 ? (
                        history.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-4 rounded-xl border border-white/5" style={{ background: 'rgba(255,255,255,0.02)' }}>
                            <div>
                              <div className="text-sm font-semibold text-white/90">{item.description}</div>
                              <div className="text-xs text-white/40 mt-0.5">{new Date(item.date).toLocaleDateString()}</div>
                            </div>
                            <div className={`font-bold text-sm ${
                              item.amount > 0 ? 'text-green-400' : 'text-white/60'
                            }`}>
                              {item.amount > 0 ? '+' : ''}{item.amount}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-white/30 py-8 text-sm">
                          Nessuna transazione trovata
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Billing Tab */}
            {activeTab === 'billing' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>
                  <div className="icon-box primary w-10 h-10 rounded-xl">
                    <CreditCard size={18} />
                  </div>
                  Metodi di Pagamento
                </h2>
                <div className="space-y-6">
                  <div className="glass p-8 rounded-[24px]">
                    <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Carte Salvate</h3>
                    <div className="text-center text-white/30 py-8 text-sm border border-dashed border-white/10 rounded-xl mb-6">
                      Nessun metodo di pagamento salvato
                    </div>
                    <button className="pill-ghost w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2">
                      <CreditCard size={16} /> Aggiungi Carta
                    </button>
                  </div>
                  
                  <div className="glass p-8 rounded-[24px]">
                    <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Fatturazione</h3>
                    <div className="text-center text-white/30 py-8 text-sm border border-dashed border-white/10 rounded-xl">
                      Nessuna fattura disponibile
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Danger Zone */}
            {activeTab === 'danger' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="text-2xl font-bold text-red-400 mb-8 flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-500/20 border border-red-500/30">
                    <AlertTriangle size={18} className="text-red-400" />
                  </div>
                  Zona Pericolosa
                </h2>
                <div className="rounded-[24px] p-8 border border-red-500/20" style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(255,255,255,0.02) 100%)' }}>
                  <h3 className="text-lg font-bold text-red-400 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Elimina Account</h3>
                  <p className="text-sm text-red-300/70 mb-6 leading-relaxed">
                    Una volta eliminato il tuo account, non potrai più recuperarlo. Tutti i tuoi dati, 
                    crediti e generazioni saranno permanentemente cancellati. Questa azione è irreversibile.
                  </p>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={loading}
                    className="flex items-center gap-2 px-8 py-3.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-xl transition-colors font-semibold text-sm disabled:opacity-50"
                  >
                    <Trash2 size={16} />
                    {loading ? 'Eliminando...' : 'Elimina Account Definitivamente'}
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}